import { Card, Typography, List, Row, Col, Divider, Skeleton, Tag } from 'antd';
import { EnvironmentOutlined, MailOutlined, PhoneOutlined, LinkedinOutlined, GlobalOutlined } from '@ant-design/icons';
import { useGetSingleTalent } from 'features/talents/hooks/useFetchTalentDetails';
import { useGetUserInfo } from 'hooks/useGetUserInfo';
import { useParams } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;

interface CVTemplateProps {
    talentId?: string;
}

const CVTemplate: React.FC<CVTemplateProps> = ({ talentId }) => {
    const params = useParams();
    const id = params.id;
    const { token } = useGetUserInfo();
    const { data: talentData, isLoading } = useGetSingleTalent({
        token,
        id: id as string,
    });

    const data = talentData?.data;

    
    if (!data) return null;

    return (
      <div style={{ maxWidth: 800, margin: "0 auto", padding: 24 }}>
        <Skeleton active loading={isLoading}>
          {/* Header Section */}
          <div style={{ marginBottom: 40 }}>
            <Title level={1} style={{ marginBottom: 8 }} color="#014D4E">
              {`${data.first_name} ${data.last_name} ${data.middle_name}`}
            </Title>
            <div style={{ marginBottom: 8 }}>
              <Row gutter={16}>
                {data.phone && (
                  <Col>
                    <PhoneOutlined /> {data.phone}
                  </Col>
                )}
                {data.email && (
                  <Col>
                    <MailOutlined /> {data.email}
                  </Col>
                )}
                {data.linkedin && (
                  <Col>
                    <LinkedinOutlined />{" "}
                    <a
                      href={data.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      LinkedIn
                    </a>
                  </Col>
                )}

                {data.city && (
                  <Col>
                    <EnvironmentOutlined /> {data.city}, {data.country}
                  </Col>
                )}
              </Row>
            </div>

            {/* Executive Summary */}
            {data.executive_summary && (
              <section style={{ marginBottom: 32, marginTop: 16 }}>
                <Title level={3} style={{ marginBottom: 16 }}>
                  EXECUTIVE SUMMARY
                </Title>
                <Paragraph>{data.executive_summary}</Paragraph>
              </section>
            )}
          </div>

          <Divider style={{ margin: "24px 0" }} />

          {/* Professional Experience */}
          <section style={{ marginBottom: 32 }}>
            <Title level={3} style={{ marginBottom: 24 }}>
              PROFESSIONAL EXPERIENCE
            </Title>
            <List
              itemLayout="vertical"
              dataSource={data.work_histories}
              renderItem={(item) => (
                <div style={{ marginBottom: 24 }}>
                  <Row justify="space-between">
                    <Col>
                      <Text strong style={{ fontSize: 16 }}>
                        {item.title}
                      </Text>
                      <br />
                      <Text type="secondary">{item.company}</Text>
                    </Col>
                    <Col>
                      <Text type="secondary">
                        {item.start_date} - {item.end_date}
                      </Text>
                    </Col>
                  </Row>
                  {item.description && (
                    <List
                      size="small"
                      dataSource={item.description.split("\n")}
                      renderItem={(desc: string) => (
                        <List.Item>{desc}</List.Item>
                      )}
                    />
                  )}
                </div>
              )}
            />
          </section>

          {/* Personality Assessment */}
          {/* {data.personality_assessment && (
                    <section style={{ marginBottom: 32 }}>
                        <Title level={3} style={{ marginBottom: 24 }}>PERSONALITY ASSESSMENT INSIGHTS</Title>
                        <Paragraph>{data.personality_assessment}</Paragraph>
                    </section>
                )} */}

          {/* Education */}
          <section style={{ marginBottom: 32 }}>
            <Title level={3} style={{ marginBottom: 24 }}>
              EDUCATION
            </Title>
            <List
              dataSource={data.education_histories}
              renderItem={(item) => (
                <div style={{ marginBottom: 16 }}>
                  <Row justify="space-between">
                    <Col>
                      <Text strong>{item.degree_id}</Text>
                      <br />
                      <Text type="secondary">{item.school}</Text>

                      {item.grade && (
                        <>
                          <br />
                          <Text>GPA: {item.grade}</Text>
                        </>
                      )}
                    </Col>
                    <Col>
                      <Text type="secondary">
                        {item.start_date} - {item.end_date}
                      </Text>
                    </Col>
                  </Row>
                </div>
              )}
            />
          </section>

          {/* Certifications */}
          {data.certifications?.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <Title level={3} style={{ marginBottom: 24 }}>
                CERTIFICATIONS
              </Title>
              <List
                dataSource={data.certifications}
                renderItem={(item) => (
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>{item.title}</Text>
                    <br />
                    <Text type="secondary">
                      {item.issuer} • {item.issued_date}
                    </Text>
                  </div>
                )}
              />
            </section>
          )}

          {/* Projects & Achievements */}
          {data.projects?.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <Title level={3} style={{ marginBottom: 24 }}>
                PROJECTS, PUBLICATIONS & KEY ACHIEVEMENTS
              </Title>
              <List
                dataSource={data.projects}
                renderItem={(item) => (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>{item.title}</Text>
                    <Paragraph>{item.description}</Paragraph>
                    {/* {item.tools && (
                                        <Text>
                                            Tools/Skills used:{' '}
                                            {item.tools.map((tool, index) => (
                                                <Tag key={index} style={{ margin: '4px 0' }}>{tool}</Tag>
                                            ))}
                                        </Text>
                                    )} */}
                  </div>
                )}
              />
            </section>
          )}

          {/* Volunteer Experience */}
          {data.volunteer_experiences?.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <Title level={3} style={{ marginBottom: 24 }}>
                VOLUNTEER EXPERIENCE
              </Title>
              <List
                dataSource={data.volunteer_experiences}
                renderItem={(item) => (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>{item.title}</Text>
                    <br />
                    <Text type="secondary">{item.company}</Text>
                    <Text type="secondary" style={{ display: "block" }}>
                      {item.start_date} - {item.end_date}
                    </Text>
                    {item.description && (
                      <List
                        size="small"
                        dataSource={item.description.split("\n")}
                        renderItem={(desc: string) => (
                          <List.Item>{desc}</List.Item>
                        )}
                      />
                    )}
                  </div>
                )}
              />
            </section>
          )}

          {/* Career Interest */}
          {data.career_interests?.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <Title level={3} style={{ marginBottom: 24 }}>
                CAREER INTEREST
              </Title>
              <div>
                {data.career_interests.map((career, index) => (
                  <Tag key={index} color="#5b8687" style={{ margin: 4 }}>
                    {career.name}
                  </Tag>
                ))}
              </div>
            </section>
          )}

          {/* Languages */}
          {data.language && (
            <section style={{ marginBottom: 32 }}>
              <Title level={3} style={{ marginBottom: 24 }}>
                LANGUAGES
              </Title>
              <div>
                {data.language.map((lang, index) => (
                  <Tag key={index} style={{ margin: 4 }}>
                    {lang}
                  </Tag>
                ))}
              </div>
            </section>
          )}
        </Skeleton>
      </div>
    );
};

export default CVTemplate;